import { EditCard, EditCardChange, EditCardState, Grid } from '@servicetitan/anvil2';
import { Children, PropsWithChildren, ReactNode, RefObject, useState } from 'react';
import { ImperativeForm } from '../common/Form';
import { ApplicationDataStore } from '../application/ApplicationDataStore';
import { editCardContainer } from './ReviewCard.module.scss';

interface ReviewCardProps {
    children: ReactNode;
    title: string;
    formRef: RefObject<ImperativeForm>;
}

export const ReviewCard = ({ children, title, formRef }: ReviewCardProps) => {
    const [cardState, setCardState] = useState<EditCardState>('complete');
    const { captureEditModeState, resetToEditModeState, setEditMode } =
        ApplicationDataStore.useStore();

    const onStateChange = (change: EditCardChange) => {
        if (change === 'edit') {
            captureEditModeState();
            setCardState('in progress');
        }
        if (change === 'save') {
            formRef.current?.submit();
            if (formRef.current?.isValid()) {
                setEditMode(false);
                setCardState('complete');
            }
        }
        if (change === 'cancel') {
            resetToEditModeState();
            setCardState('complete');
        }
    };
    return (
        <EditCard
            state={cardState}
            onStateChange={onStateChange}
            headerText={title}
            flexDirection="column"
            style={{ padding: '24px', width: '100%' }}
            role="listitem"
            className={editCardContainer}
        >
            <Grid rowGap="5" templateColumns="repeat(auto-fit, minmax(265px, 1fr))">
                {Children.map(children, child =>
                    cardState === 'in progress' && isEditChild(child)
                        ? child
                        : cardState === 'complete' && isViewChild(child)
                          ? child
                          : null,
                )}
            </Grid>
        </EditCard>
    );
};

// Type guard to check if a child is of type ReviewCard.Edit
function isEditChild(child: ReactNode): child is ReactNode {
    return (
        child != null &&
        typeof child === 'object' &&
        'type' in child &&
        child.type === ReviewCard.Edit
    );
}

// Type guard to check if a child is of type ReviewCard.View
function isViewChild(child: ReactNode): child is ReactNode {
    return (
        child != null &&
        typeof child === 'object' &&
        'type' in child &&
        child.type === ReviewCard.View
    );
}

ReviewCard.Edit = ({ children }: PropsWithChildren) => children;
ReviewCard.View = ({ children }: PropsWithChildren) => children;
